import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import playfieldLogo from "@/assets/playfield-logo.png";

const Navigation = () => {
  // Initialize with current scroll position to prevent flash
  const [isScrolled, setIsScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 50
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language].nav;

  useEffect(() => {
    // Check scroll position immediately on mount/route change
    setIsScrolled(window.scrollY > 50);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const handleNavigation = (path: string, sectionId?: string) => {
    setIsMobileMenuOpen(false);
    
    if (path === location.pathname) {
      // We're already on this page
      if (sectionId) {
        // Scroll to specific section on same page
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // No section specified, scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else if (sectionId) {
      // Different page, navigate then scroll to section
      navigate(path);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      // Different page, just navigate
      navigate(path);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-6 lg:gap-8">
            <button
              type="button"
              onClick={() => handleNavigation("/")}
              className="flex items-center gap-2.5 text-foreground hover:text-primary transition-colors duration-200 outline-none focus:outline-none focus:ring-0 select-none"
              aria-label="PlayfieldVR home"
            >
              <img
                src={playfieldLogo}
                alt="PlayfieldVR"
                className="h-12 md:h-14 w-auto"
              />
            </button>
            
            {/* Mobile Service Badge - Desktop Only */}
            <div className="hidden lg:block">
              <Badge
                className="bg-primary text-primary-foreground px-4 py-1.5 text-sm font-bold border-0 cursor-pointer hover:bg-primary/90 transition-all duration-300"
                onClick={() => handleNavigation("/", "contact-form")}
              >
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
                {t.vrAtLocation}
              </Badge>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleNavigation("/")}
              className={`transition-colors duration-200 font-medium ${
                location.pathname === "/" 
                  ? "text-primary border-b-2 border-primary pb-1" 
                  : "text-foreground hover:text-primary"
              }`}
            >
              {t.home}
            </button>
            <button
              onClick={() => handleNavigation("/experiences")}
              className={`transition-colors duration-200 font-medium ${
                location.pathname === "/experiences" 
                  ? "text-primary border-b-2 border-primary pb-1" 
                  : "text-foreground hover:text-primary"
              }`}
            >
              {t.experiences}
            </button>
            <button
              onClick={() => handleNavigation("/", "about")}
              className="text-foreground hover:text-primary transition-colors duration-200"
            >
              {t.about}
            </button>
            <button
              onClick={() => handleNavigation("/", "contact-form")}
              className="text-foreground hover:text-primary transition-colors duration-200"
            >
              {t.contact}
            </button>
            <LanguageSwitcher />
            <Button
              onClick={() => handleNavigation("/", "contact-form")}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all duration-300"
            >
              {t.getStarted}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-border animate-fade-in bg-background/98 backdrop-blur-md shadow-lg">
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => handleNavigation("/")}
                className={`transition-all duration-200 text-left font-medium py-3 px-4 rounded-lg ${
                  location.pathname === "/" 
                    ? "text-primary bg-primary/10 border-l-4 border-primary" 
                    : "text-foreground hover:text-primary hover:bg-muted/50"
                }`}
              >
                {t.home}
              </button>
              <button
                onClick={() => handleNavigation("/experiences")}
                className={`transition-all duration-200 text-left font-medium py-3 px-4 rounded-lg ${
                  location.pathname === "/experiences" 
                    ? "text-primary bg-primary/10 border-l-4 border-primary" 
                    : "text-foreground hover:text-primary hover:bg-muted/50"
                }`}
              >
                {t.experiences}
              </button>
              <button
                onClick={() => handleNavigation("/", "about")}
                className="text-foreground hover:text-primary hover:bg-muted/50 transition-all duration-200 text-left py-3 px-4 rounded-lg"
              >
                {t.about}
              </button>
              <button
                onClick={() => handleNavigation("/", "contact-form")}
                className="text-foreground hover:text-primary hover:bg-muted/50 transition-all duration-200 text-left py-3 px-4 rounded-lg"
              >
                {t.contact}
              </button>
              <div className="flex justify-center pt-4 pb-2">
                <LanguageSwitcher />
              </div>
              <Button
                onClick={() => handleNavigation("/", "contact-form")}
                className="bg-primary text-primary-foreground font-semibold w-full py-3 transition-all duration-300"
              >
                {t.getStarted}
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
